/**
 * Review display formatters — M11-A (plan 320/322, review capture + display).
 *
 * Pure, side-effect-free string formatters over the buyer-facing
 * `DisplayReview` wire shape (backend/internal/services/marketplace/reviews/display.go).
 *
 * `reviewMetaLine` NEVER re-derives the date format client-side — the
 * server already renders `reviewer_line` as "Verified buyer · Jan 2006"
 * (Go time format, real U+00B7 middot); this only appends provenance + the
 * edited marker to that server-rendered string verbatim.
 *
 * Separators here MUST be the same "·" the backend emits. Joining with an
 * ASCII hyphen produces a visibly mixed line against live data
 * ("Verified buyer · Mar 2026 - Marketplace connection").
 */

import {
  formatDollars,
  formatPercent,
  itemLabel,
} from "@sill/shared/components/marketplace/offer-card";

export type ReviewOrigin = "marketplace" | "invited";
export type ReviewLabel = "worked_together" | "closed";

export interface DisplayReview {
  stars: number;
  body: string;
  label: ReviewLabel;
  origin: ReviewOrigin;
  created_at: string;
  reviewer_line: string;
}

const ORIGIN_PROVENANCE: Record<ReviewOrigin, string> = {
  marketplace: "Marketplace connection",
  invited: "Verified client",
};

const LABEL_CHIP: Record<ReviewLabel, string> = {
  closed: "CLOSED A DEAL \u2713",
  worked_together: "WORKED TOGETHER",
};

/**
 * Builds the buyer-facing review meta line: the server's own
 * `reviewer_line` (already-formatted date) with the origin provenance
 * appended. Reviews are never edited, so there is no edit marker.
 */
export function reviewMetaLine(
  review: Pick<DisplayReview, "reviewer_line" | "origin">,
): string {
  return `${review.reviewer_line} \u00b7 ${ORIGIN_PROVENANCE[review.origin]}`;
}

/**
 * Maps a review's maturity label to its display chip text.
 */
export function reviewLabelChip(label: ReviewLabel): string {
  return LABEL_CHIP[label];
}

/**
 * Derives the close-upgrade chip's incentive phrase from the buyer's frozen
 * accepted offer — the "{incentive}" in "Was your {incentive} honored?".
 *
 * Returns undefined when the connection carried no incentive at all, which
 * is the signal for `ReviewCloseUpgradeModal` to omit the chip entirely
 * rather than ask about something that was never promised.
 *
 * Vocabulary is reused from `@sill/shared`'s offer-card helpers on purpose:
 * the buyer already saw these exact words pre-accept, and a second label set
 * here would let the review ask describe the offer differently than the
 * offer did.
 */
export function incentiveLabelFromOffer(
  offer:
    | {
        rebate?: { type: string; value?: number }[];
        cash?: { type: string; value?: number }[];
        perks?: { type: string; value?: number }[];
      }
    | undefined,
): string | undefined {
  if (!offer) return undefined;

  const cashTotal = (offer.cash ?? []).reduce(
    (sum, item) => sum + (item.value ?? 0),
    0,
  );
  if (cashTotal > 0) return `${formatDollars(cashTotal)} in credits`;

  const rebate = (offer.rebate ?? []).find((item) => (item.value ?? 0) > 0);
  if (rebate?.value != null) {
    return `${formatPercent(rebate.value)} ${itemLabel(rebate.type).toLowerCase()}`;
  }

  const perk = (offer.perks ?? [])[0];
  if (perk) return itemLabel(perk.type).toLowerCase();

  return undefined;
}
